import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Stack, Button, Avatar, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment,
  IconButton, Tooltip, CircularProgress, Skeleton,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton, alpha,
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';
import { fetchContractFields, fillHtmlTemplate } from '../../utils/contractFill';
import { downloadContractDocument } from '../../utils/wordTemplate';
import dateFormats from '../../services/dateFormats';
import configService from '../../services/configService';

/* ─────────────────────────────────────────────────────────────────────────────
 * Design tokens (Nexuma teal brand)
 * ──────────────────────────────────────────────────────────────────────────── */
const T = {
  brand: '#3D8FA3',
  brandHover: '#327C8E',
  brandLight: '#E7F4F7',
  brandSoft: '#F2FAFC',

  ink: '#0F172A',
  ink700: '#334155',
  ink500: '#64748B',
  ink400: '#94A3B8',
  ink300: '#CBD5E1',
  line: '#E2E8F0',
  surface: '#F8FAFC',
  surfaceAlt: '#F1F5F9',
  white: '#FFFFFF',

  success: '#10B981',
  successBg: '#ECFDF5',
  successInk: '#059669',
  warning: '#F59E0B',
  warningBg: '#FEF3C7',
  warningInk: '#92400E',
  error: '#EF4444',
  errorBg: '#FEF2F2',
  errorInk: '#DC2626',
};

const cardSx = {
  bgcolor: T.white,
  borderRadius: '16px',
  border: `1px solid ${T.line}`,
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Tiny presentational helpers
 * ──────────────────────────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, tint, ink }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 140,
        px: 2.5,
        py: 2,
        borderRadius: '14px',
        bgcolor: T.white,
        border: `1px solid ${T.line}`,
        boxShadow: '0 1px 2px rgba(15,23,42,.03)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: tint, color: ink,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.ink500, letterSpacing: 0.1 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.1, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function StatusBadge({ color, label }) {
  const map = {
    success: { bg: T.successBg, fg: T.successInk, dot: T.success },
    warning: { bg: T.warningBg, fg: T.warningInk, dot: T.warning },
    error:   { bg: T.errorBg,   fg: T.errorInk,   dot: T.error },
    default: { bg: T.surfaceAlt, fg: T.ink500, dot: T.ink400 },
  };
  const c = map[color] || map.default;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
      height: 24, px: 1.25, borderRadius: '999px',
      bgcolor: c.bg, color: c.fg,
      fontSize: 12, fontWeight: 600, lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.dot }} />
      {label}
    </Box>
  );
}

function IconBtn({ title, onClick, disabled, children, to, color }) {
  const sx = {
    width: 36, height: 36, borderRadius: '10px',
    color: color || T.ink500,
    transition: 'all 160ms ease',
    '&:hover': { bgcolor: T.surfaceAlt, color: color || T.ink },
    '&.Mui-disabled': { color: T.ink300 },
  };
  const inner = (
    <IconButton size="small" onClick={onClick} disabled={disabled} sx={sx} component={to ? Link : 'button'} to={to}>
      {children}
    </IconButton>
  );
  return title ? <Tooltip title={title} arrow placement="top">{disabled ? <span>{inner}</span> : inner}</Tooltip> : inner;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Main component
 * ──────────────────────────────────────────────────────────────────────────── */
export default function ContractsList() {
  const [workers, setWorkers] = useState([]);
  const [toast, setToast] = useState({ open: false, message: '', variant: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loader, setLoader] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const fileInputRef = useRef(null);

  // ── Custom fields (contracts module) ─────────────────────────────────────
  const [contractCFDefs, setContractCFDefs]     = useState([]);
  const [contractCFValuesMap, setContractCFValuesMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const mod = await configService.getModuleByKey('contracts');
        setContractCFDefs((mod?.data?.fields || []).filter(f => f.is_active && f.show_in_list));
        const vals = await configService.getAllCustomFieldValues('contracts');
        setContractCFValuesMap(vals?.data || vals || {});
      } catch (_) { /* fail gracefully — no custom fields configured */ }
    })();
  }, []);

  const formatCFValue = (field, raw) => {
    if (raw === undefined || raw === null || raw === '') return '—';
    if (field.field_type === 'boolean') return raw === 'true' || raw === true ? 'Po' : 'Jo';
    if (field.field_type === 'date')    return dateFormats ? dateFormats(raw) : raw;
    if (field.field_type === 'select' || field.field_type === 'multiselect') {
      const opts = field.options || [];
      const vals = String(raw).split(',').map(v => v.trim());
      const labels = vals.map(v => opts.find(o => String(o.value) === v)?.label || v);
      return labels.join(', ');
    }
    const s = String(raw);
    return s.length > 60 ? s.slice(0, 60) + '…' : s;
  };

  const rawCFValue = (field, raw) => {
    if (raw === undefined || raw === null || raw === '') return '';
    if (field.field_type === 'select' || field.field_type === 'multiselect') {
      const opts = field.options || [];
      const vals = String(raw).split(',').map(v => v.trim());
      return vals.map(v => opts.find(o => String(o.value) === v)?.label || v).join(', ');
    }
    return String(raw);
  };
  const workerTags = ["FirstName", "LastName", "ParentName", "PersonalId", "Address", "Phone", "PostalCode", "BankAccount", "BankName", "Birthday", "Country","Place", "Email", "Gender", "MaritalStatus", "Qualification", "Profession", "ContractType", "StartDate", "EndDate", "WorkType", "SalaryType", "Department", "BaseSalary", "BrutoNeto", "HourSalary", "HourSalaryWeekend", "HourSalaryHoliday", "Image", "TermsOfContract"];

  // Company-profile placeholders → field in the `settings` record. Prefixed with
  // "Company" so they never collide with the worker fields of the same name
  // (Address, Email, Phone, BankName…). The employer block in a template is filled
  // from the company profile instead of being hardcoded.
  const COMPANY_TAGS = {
    CompanyName:        'CompanyName',
    CompanyNUI:         'NUI',
    CompanyFiscalNumber:'FiscalNumber',
    CompanyAddress:     'Address',
    CompanyCity:        'City',
    CompanyCountry:     'Country',
    CompanyPhone:       'Phone',
    CompanyEmail:       'Email',
    CompanyWebsite:     'Website',
    CompanyBankName:    'BankName',
    CompanyBankAccount: 'BankAccount',
    CompanyDirector:    'Director',
  };

  const normalizeWorker = (w = {}) => ({
    ...w,
    Id: w?.Id ?? w?.id,
    FirstName: w?.FirstName ?? w?.first_name ?? w?.firstname ?? w?.firstName,
    ParentName: w?.ParentName ?? w?.parent_name ?? w?.parentName,
    LastName: w?.LastName ?? w?.last_name ?? w?.lastname ?? w?.lastName,
    Email: w?.Email ?? w?.email,
    Phone: w?.Phone ?? w?.phone,
    Place: w?.Place ?? w?.place,
  });

  const normalizeContract = (c = {}) => ({
    ...c,
    Id: c?.Id ?? c?.id,
    WorkerId: c?.WorkerId ?? c?.worker_id ?? c?.workerId ?? c?.Employee ?? c?.employee ?? c?.employee_id,
    StartDate: c?.StartDate ?? c?.start_date ?? c?.startDate,
    EndDate: c?.EndDate ?? c?.end_date ?? c?.endDate,
    Template: c?.Template ?? c?.template_id ?? c?.template,
    ContractType: c?.ContractType ?? c?.contract_type ?? c?.contractType,
    WorkType: c?.WorkType ?? c?.work_type ?? c?.employment_type,
    SalaryType: c?.SalaryType ?? c?.salary_type ?? c?.salaryType,
    Department: c?.Department ?? c?.department,
    BranchId: c?.BranchId ?? c?.branch_id ?? c?.branch,
    BaseSalary: c?.BaseSalary ?? c?.base_salary ?? c?.baseSalary,
    BrutoNeto: c?.BrutoNeto ?? c?.bruto_neto ?? c?.brutoNeto ?? c?.brutoneto,
    HourSalary: c?.HourSalary ?? c?.hour_salary ?? c?.hourSalary,
    HourSalaryWeekend: c?.HourSalaryWeekend ?? c?.hour_salary_weekend ?? c?.hourSalaryWeekend,
    HourSalaryHoliday: c?.HourSalaryHoliday ?? c?.hour_salary_holiday ?? c?.hourSalaryHoliday,
    TermsOfContract: c?.TermsOfContract ?? c?.terms_of_contract ?? c?.terms,
    ContractFile: c?.ContractFile ?? c?.contract_file,
    ContractDocument: c?.ContractDocument ?? c?.contract_document,
    ContractFileName: c?.ContractFileName ?? c?.contract_filename,
    ContractDocumentName: c?.ContractDocumentName ?? c?.contract_document_name,
    ContractFileUploadedAt: c?.ContractFileUploadedAt ?? c?.contract_file_uploaded_at,
    SignedContractFile: c?.SignedContractFile ?? c?.signed_contract_file,
    SignedContractName: c?.SignedContractName ?? c?.signed_contract_name,
    SignedContractType: c?.SignedContractType ?? c?.signed_contract_type,
    SignedContractUploadedAt: c?.SignedContractUploadedAt ?? c?.signed_contract_uploaded_at,
  });

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(+e.target.value); setPage(0); };

  const triggerHiddenInput = (worker) => {
    if (!worker?.Contract?.Id) {
      setToast({ open: true, message: 'Kjo kontratë nuk mund të azhurnohet.', variant: 'error' });
      return;
    }
    setUploadTarget(worker);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const resolveSignedContract = (contract) => {
    if (!contract) return null;
    const data = contract.SignedContractFile || contract.SignedContract || contract.ContractFile || contract.ContractDocument;
    if (!data) return null;
    return {
      data,
      name: contract.SignedContractName || contract.ContractFileName || contract.ContractDocumentName || 'kontrata',
      uploadedAt: contract.SignedContractUploadedAt || contract.ContractFileUploadedAt,
      type: contract.SignedContractType || contract.ContractFileType,
    };
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const downloadBase64File = (base64, name = 'kontrata') => {
    try {
      const parts = base64.split(',');
      const mimeMatch = parts[0]?.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const b64Data = parts.length > 1 ? parts[1] : parts[0];
      const byteCharacters = atob(b64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = name;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      setToast({ open: true, message: 'Nuk u arrit të shkarkohet dokumenti.', variant: 'error' });
    }
  };

  const handleDownloadSigned = (contract) => {
    const file = resolveSignedContract(contract);
    if (!file) {
      setToast({ open: true, message: 'Kontrata nuk është ngarkuar ende.', variant: 'warning' });
      return;
    }
    downloadBase64File(file.data, file.name || 'kontrata');
  };

  const handleFileUpload = async (event) => {
    const file = event.target?.files?.[0];
    if (!file || !uploadTarget?.Contract?.Id) { event.target.value = ''; return; }
    if (file.size > 8 * 1024 * 1024) {
      setToast({ open: true, message: 'Skedari duhet të jetë më i vogël se 8MB.', variant: 'error' });
      event.target.value = ''; return;
    }
    try {
      setUploadingId(uploadTarget.Contract.Id);
      const base64 = await readFileAsDataUrl(file);
      const contractResponse = await apiService().query('getContract', 'id=' + uploadTarget.Contract.Id);
      if (!contractResponse?.success || !contractResponse?.data?.length) throw new Error('Kontrata nuk u gjet.');
      const fullContract = contractResponse.data[0];
      const payload = {
        ...fullContract,
        SignedContractFile: base64,
        SignedContractName: file.name,
        SignedContractType: file.type || '',
        SignedContractUploadedAt: new Date().toISOString(),
      };
      const response = await apiService().put('workerContract', payload);
      if (!response?.success) throw new Error(response?.errorMessage || 'Ngarkimi dështoi.');
      setToast({ open: true, message: 'Kontrata u ngarkua me sukses.', variant: 'success' });
      setWorkers((prev) => prev.map((w) =>
        w.Id === uploadTarget.Id ? { ...w, Contract: { ...w.Contract, ...payload } } : w
      ));
    } catch (error) {
      setToast({ open: true, message: error?.message || 'Ngarkimi dështoi.', variant: 'error' });
    } finally {
      setUploadingId(null);
      setUploadTarget(null);
      event.target.value = '';
    }
  };

  const replaceText = (contractTemplate, employee, company) => {
    let text = contractTemplate?.Content || contractTemplate?.Text || '';
    const data = { ...(employee || {}), ...(employee?.Contract || {}) };
    // Replace longer tags first so prefixes don't get partially consumed
    // (e.g. @HourSalary must not eat the start of @HourSalaryWeekend).
    [...workerTags].sort((a, b) => b.length - a.length).forEach((tag) => {
      const value = data[tag];
      if (tag === 'Image') {
        const imgHtml = value ? `<img src="${value}" alt="Foto e punonjësit" />` : '--';
        text = text.replaceAll(`@${tag}`, imgHtml);
      } else {
        text = text.replaceAll(`@${tag}`, value || '--');
      }
    });
    // Employer placeholders, filled from the company profile (settings).
    const co = company || {};
    Object.entries(COMPANY_TAGS).forEach(([tag, field]) => {
      text = text.replaceAll(`@${tag}`, co[field] || '--');
    });
    const logoHtml = co.Logo ? `<img src="${co.Logo}" alt="logo" style="max-height:64px" />` : '';
    text = text.replaceAll('@CompanyLogo', logoHtml);
    return text;
  };

  const towordDoc = (filename = '', contractTemplate, employee, company) => {
    var header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title></head><body>";
    var footer = "</body></html>";
    var html = header + replaceText(contractTemplate, employee, company) + footer;
    var blob = new Blob(['﻿', html], { type: 'application/msword' });
    var url = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(html);
    filename = filename ? filename + '.doc' : 'document.docx';
    var downloadLink = document.createElement("a");
    document.body.appendChild(downloadLink);
    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      downloadLink.href = url; downloadLink.download = filename; downloadLink.click();
    }
    document.body.removeChild(downloadLink);
  };

  const getContractTemplate = async (employee) => {
    const contractId = employee?.Contract?.Id;
    if (!contractId) {
      setToast({ open: true, message: 'Kontrata nuk u gjet për këtë punonjës.', variant: 'error' });
      return;
    }
    try {
      const [templateRes, contractRes, companyRes] = await Promise.all([
        apiService().query('getTemplate', 'id=' + (employee?.Contract?.Template || '')),
        apiService().query('getContract', 'id=' + contractId),
        apiService().get('settings'),
      ]);
      const templateData = (templateRes?.data && templateRes.data[0]) || (Array.isArray(templateRes) && templateRes[0]) || templateRes;
      const contractDataRaw = (contractRes?.data && contractRes.data[0]) || (Array.isArray(contractRes) && contractRes[0]) || contractRes;
      const companyData = (companyRes?.data && companyRes.data[0]) || (Array.isArray(companyRes) && companyRes[0]) || companyRes || {};
      const normalizedContract = normalizeContract(contractDataRaw);
      if (!templateData) {
        setToast({ open: true, message: 'Template nuk u gjet.', variant: 'error' });
        return;
      }
      // Shablloni Word: kontrata del nga dokumenti origjinal, me dizajnin e vet.
      if (templateData.HasDocx) {
        await downloadContractDocument(contractId, 'docx', templateData.Id ?? templateData.id);
        return;
      }
      const employeeWithContract = { ...employee, Contract: normalizedContract };
      const fields = await fetchContractFields(contractId);
      if (fields) {
        towordDoc(`${employee?.FirstName || ''}_${employee?.LastName || ''}`,
          { ...templateData, Content: fillHtmlTemplate(templateData.Content || templateData.Text || '', fields) },
          employeeWithContract, companyData);
        return;
      }
      towordDoc(`${employee?.FirstName || ''}_${employee?.LastName || ''}`, templateData, employeeWithContract, companyData);
    } catch (error) {
      setToast({ open: true, message: error?.message || 'Nuk u arrit të merren të dhënat e kontratës.', variant: 'error' });
      // eslint-disable-next-line no-console
      console.error('getContractTemplate error', error);
    }
  };

  useEffect(() => {
    apiService()
      .get('workers')
      .then((response) => {
        const raw = response?.data || response || [];
        if (!Array.isArray(raw)) { setLoader(false); return; }
        const list = raw.map(normalizeWorker);
        const fetches = list.map((e) =>
          apiService()
            .query('getWorkerContracts', 'id=' + (e?.Id ?? e?.id ?? ''))
            .then((wc) => {
              const contracts = wc?.data || wc || [];
              const normalizedContracts = Array.isArray(contracts) ? contracts.map(normalizeContract) : [];
              return { ...e, Contract: normalizedContracts[normalizedContracts.length - 1] || null };
            })
            .catch(() => e)
        );
        Promise.all(fetches).then((res) => { setWorkers(res); setLoader(false); });
      })
      .catch(() => setLoader(false));
  }, []);

  const prioritized = useMemo(() => {
    const now = new Date();
    const DAY = 1000 * 60 * 60 * 24;
    function statusOf(contract) {
      if (!contract || !contract.EndDate) return { rank: 0, label: '—', color: 'default', daysLeft: Infinity };
      const end = new Date(contract.EndDate);
      const diffDays = Math.floor((end - now) / DAY);
      if (isNaN(diffDays)) return { rank: 0, label: '—', color: 'default', daysLeft: Infinity };
      if (diffDays < 0) return { rank: 2, label: 'Skaduar', color: 'error', daysLeft: diffDays };
      if (diffDays <= 30) return { rank: 1, label: 'Skadon brenda 30 ditëve', color: 'warning', daysLeft: diffDays };
      return { rank: 0, label: 'Aktive', color: 'success', daysLeft: diffDays };
    }
    const withMeta = (workers || []).map((w) => ({ ...w, _status: statusOf(w.Contract) }));
    withMeta.sort((a, b) => {
      if (b._status.rank !== a._status.rank) return b._status.rank - a._status.rank;
      return (a._status.daysLeft || 0) - (b._status.daysLeft || 0);
    });
    return withMeta;
  }, [workers]);

  const filtered = useMemo(() => {
    let list = prioritized;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(w => {
        const coreMatch =
          `${w.FirstName || ''} ${w.LastName || ''}`.toLowerCase().includes(q) ||
          (w.Email || '').toLowerCase().includes(q);
        if (coreMatch) return true;
        const cfVals = contractCFValuesMap[String(w.Contract?.Id)] || {};
        return contractCFDefs
          .filter(f => f.is_searchable !== false)
          .some(f => String(cfVals[f.key] || '').toLowerCase().includes(q));
      });
    }
    if (statusFilter === 'active')   list = list.filter(w => w._status?.color === 'success');
    if (statusFilter === 'warning')  list = list.filter(w => w._status?.color === 'warning');
    if (statusFilter === 'expired')  list = list.filter(w => w._status?.color === 'error');
    if (statusFilter === 'none')     list = list.filter(w => !w.Contract);
    return list;
  }, [prioritized, search, statusFilter, contractCFDefs, contractCFValuesMap]);

  const exportFields = contractCFDefs.filter(f => f.is_exportable !== false);

  const handleExport = () => {
    const header = [
      'Punonjësi', 'Email', 'Data Fillimit', 'Data Mbarimit', 'Statusi',
      ...exportFields.map(f => f.label),
    ];
    const rows = filtered.map(w => [
      `${w.FirstName || ''} ${w.LastName || ''}`.trim(),
      w.Email || '',
      w.Contract?.StartDate ? dateFormats(w.Contract.StartDate) : '',
      w.Contract?.EndDate   ? dateFormats(w.Contract.EndDate)   : '',
      w._status?.label || '',
      ...exportFields.map(f => rawCFValue(f, (contractCFValuesMap[String(w.Contract?.Id)] || {})[f.key])),
    ]);
    const escape = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'kontratat.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => ({
    total:   prioritized.length,
    active:  prioritized.filter(w => w._status?.color === 'success').length,
    warning: prioritized.filter(w => w._status?.color === 'warning').length,
    expired: prioritized.filter(w => w._status?.color === 'error').length,
  }), [prioritized]);

  const hasFilters = !!(search || statusFilter);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setPage(0); };

  const initialsOf = (w) => `${(w.FirstName || '')[0] || ''}${(w.LastName || '')[0] || ''}`.toUpperCase() || '—';

  /* ── Column header style — calm, sentence-case ──────────────────────────── */
  const thSx = {
    fontSize: 12,
    fontWeight: 600,
    color: T.ink400,
    bgcolor: T.surface,
    borderBottom: `1px solid ${T.line}`,
    py: 1.5,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
  };
  const tdSx = {
    borderBottom: `1px solid ${T.surfaceAlt}`,
    py: 2,
    fontSize: 14,
    color: T.ink700,
  };

  const segmentSx = {
    bgcolor: T.surface,
    borderRadius: '12px',
    p: 0.5,
    border: `1px solid ${T.line}`,
    gap: 0.25,
    '& .MuiToggleButton-root': {
      border: 'none',
      borderRadius: '8px !important',
      px: 1.75,
      py: 0.75,
      fontSize: 13,
      fontWeight: 500,
      color: T.ink500,
      textTransform: 'none',
      transition: 'all 160ms ease',
      '&:hover': { bgcolor: T.white, color: T.ink },
      '&.Mui-selected': {
        bgcolor: T.white,
        color: T.ink,
        fontWeight: 600,
        boxShadow: '0 1px 2px rgba(15,23,42,.06)',
        '&:hover': { bgcolor: T.white },
      },
    },
  };

  return (
    <Box sx={{ bgcolor: T.surface, minHeight: '100%', p: { xs: 2, md: 3 } }}>
      {/* hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* ─────────── KPI row ─────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
        <StatCard
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Gjithsej"
          value={loader ? <Skeleton width={40} /> : stats.total}
          tint={T.brandLight}
          ink={T.brand}
        />
        <StatCard
          icon={<CheckCircleRoundedIcon sx={{ fontSize: 18 }} />}
          label="Aktive"
          value={loader ? <Skeleton width={40} /> : stats.active}
          tint={T.successBg}
          ink={T.successInk}
        />
        <StatCard
          icon={<AccessTimeRoundedIcon sx={{ fontSize: 18 }} />}
          label="Skadon së shpejti"
          value={loader ? <Skeleton width={40} /> : stats.warning}
          tint={T.warningBg}
          ink={T.warningInk}
        />
        <StatCard
          icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />}
          label="Skaduar"
          value={loader ? <Skeleton width={40} /> : stats.expired}
          tint={T.errorBg}
          ink={T.errorInk}
        />
      </Stack>

      {/* ─────────── Table card ─────────── */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{
          px: 2.5, py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${T.line}`,
        }}>
          <TextField
            size="small"
            placeholder="Kërko sipas emrit ose email-it…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            sx={{
              width: { xs: '100%', sm: 320 },
              '& .MuiOutlinedInput-root': {
                height: 44,
                borderRadius: '12px',
                bgcolor: T.surface,
                fontSize: 14,
                transition: 'all 160ms ease',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover': { bgcolor: T.surfaceAlt },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused': {
                  bgcolor: T.white,
                  boxShadow: `0 0 0 3px ${alpha(T.brand, 0.15)}`,
                },
                '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: T.ink400 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.5 }}>
                    <CloseRoundedIcon sx={{ fontSize: 16, color: T.ink400 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, v) => { if (v !== null) { setStatusFilter(v); setPage(0); } }}
            sx={segmentSx}
          >
            <ToggleButton value="">Të gjitha</ToggleButton>
            <ToggleButton value="active">Aktive</ToggleButton>
            <ToggleButton value="warning">Skadon</ToggleButton>
            <ToggleButton value="expired">Skaduar</ToggleButton>
            <ToggleButton value="none">Pa kontratë</ToggleButton>
          </ToggleButtonGroup>

          {hasFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              startIcon={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 32,
                borderRadius: '8px',
                px: 1.25,
                textTransform: 'none',
                fontSize: 13,
                color: T.ink500,
                '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
              }}
            >
              Pastro
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          <Typography sx={{ fontSize: 13, color: T.ink500, fontVariantNumeric: 'tabular-nums' }}>
            {loader ? '—' : `${filtered.length} rezultate`}
          </Typography>

          <Button
            onClick={handleExport}
            startIcon={<IosShareRoundedIcon sx={{ fontSize: 16 }} />}
            disableElevation
            sx={{
              height: 36,
              px: 1.75,
              borderRadius: '10px',
              bgcolor: T.white,
              color: T.ink700,
              border: `1px solid ${T.line}`,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 500,
              '&:hover': { bgcolor: T.surface, borderColor: T.ink300, color: T.ink },
            }}
          >
            Eksporto
          </Button>

          <Button
            component={Link}
            to="/contractlist/addcontract"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            disableElevation
            sx={{
              height: 36,
              px: 1.75,
              borderRadius: '10px',
              bgcolor: T.brand,
              color: T.white,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              '&:hover': { bgcolor: T.brandHover },
            }}
          >
            Shto Kontratë
          </Button>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, pl: 2.5 }}>Punonjësi</TableCell>
                <TableCell sx={{ ...thSx, display: { xs: 'none', sm: 'table-cell' } }}>Periudha</TableCell>
                <TableCell sx={thSx}>Statusi</TableCell>
                <TableCell sx={{ ...thSx, display: { xs: 'none', md: 'table-cell' } }}>Dok. i nënshkruar</TableCell>
                <TableCell sx={{ ...thSx, display: { xs: 'none', lg: 'table-cell' } }}>Word</TableCell>
                {contractCFDefs.map(f => (
                  <TableCell key={f.key} sx={{ ...thSx, display: { xs: 'none', xl: 'table-cell' } }}>{f.label}</TableCell>
                ))}
                <TableCell sx={{ ...thSx, pr: 2.5 }} align="right">Veprime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loader ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ ...tdSx, pl: 2.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box>
                          <Skeleton width={140} height={16} />
                          <Skeleton width={180} height={14} sx={{ mt: 0.5 }} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, display: { xs: 'none', sm: 'table-cell' } }}><Skeleton width={120} /></TableCell>
                    <TableCell sx={tdSx}><Skeleton width={80} height={24} sx={{ borderRadius: 99 }} /></TableCell>
                    <TableCell sx={{ ...tdSx, display: { xs: 'none', md: 'table-cell' } }}><Skeleton width={100} /></TableCell>
                    <TableCell sx={{ ...tdSx, display: { xs: 'none', lg: 'table-cell' } }}><Skeleton width={40} /></TableCell>
                    <TableCell sx={{ ...tdSx, pr: 2.5 }} align="right"><Skeleton width={80} /></TableCell>
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7 + contractCFDefs.length} sx={{ border: 0, py: 10 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box sx={{
                        width: 56, height: 56, borderRadius: '14px',
                        bgcolor: T.brandLight, color: T.brand,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <ArticleOutlinedIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.ink }}>
                        Nuk u gjet asnjë kontratë
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: T.ink500, textAlign: 'center', maxWidth: 360 }}>
                        {hasFilters
                          ? 'Provo të heqësh disa filtra ose të kërkosh me një term tjetër.'
                          : 'Sapo të krijosh kontrata për punonjësit, do t’i shohësh këtu.'}
                      </Typography>
                      {hasFilters && (
                        <Button
                          onClick={clearFilters}
                          sx={{
                            mt: 1, textTransform: 'none', borderRadius: '10px',
                            color: T.brand, fontSize: 13, fontWeight: 600,
                            '&:hover': { bgcolor: T.brandSoft },
                          }}
                        >
                          Pastro filtrat
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((w, index) => {
                  const signedContract = resolveSignedContract(w.Contract);
                  const isUploading = uploadingId === w?.Contract?.Id;
                  const rowKey = w?.Id ?? `${w.FirstName || 'row'}-${index}`;
                  const sc = w._status?.color;
                  const statusLabel =
                    sc === 'success' ? 'Aktive'
                    : sc === 'warning' ? `Skadon · ${isFinite(w._status?.daysLeft) ? `${w._status.daysLeft}d` : ''}`.trim().replace(/·\s*$/, '')
                    : sc === 'error'   ? 'Skaduar'
                    : 'Pa kontratë';

                  return (
                    <TableRow
                      key={rowKey}
                      sx={{
                        transition: 'background 180ms ease',
                        '&:hover': { bgcolor: T.surface },
                        '&:last-of-type td': { borderBottom: 0 },
                      }}
                    >
                      {/* Employee */}
                      <TableCell sx={{ ...tdSx, pl: 2.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{
                            width: 40, height: 40,
                            fontSize: 13, fontWeight: 600,
                            bgcolor: T.brandLight, color: T.brand,
                          }}>
                            {initialsOf(w)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, lineHeight: 1.3 }} noWrap>
                              {w.FirstName} {w.LastName}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: T.ink500, lineHeight: 1.3, mt: 0.25 }} noWrap>
                              {w.Email || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Period */}
                      <TableCell sx={{ ...tdSx, display: { xs: 'none', sm: 'table-cell' }, fontVariantNumeric: 'tabular-nums' }}>
                        {w.Contract ? (
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontSize: 13.5, color: T.ink700, fontWeight: 500 }}>
                              {w.Contract.StartDate ? dateFormats(w.Contract.StartDate) : '—'}
                              <Box component="span" sx={{ color: T.ink300, mx: 0.75 }}>→</Box>
                              {w.Contract.EndDate ? dateFormats(w.Contract.EndDate) : '—'}
                            </Typography>
                            {isFinite(w._status?.daysLeft) && w._status?.color !== 'default' && (
                              <Typography sx={{ fontSize: 12, color: T.ink400 }}>
                                {w._status.daysLeft >= 0 ? `${w._status.daysLeft} ditë të mbetura` : `Skaduar ${Math.abs(w._status.daysLeft)} ditë më parë`}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontSize: 13.5, color: T.ink400 }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={tdSx}>
                        <StatusBadge color={sc} label={statusLabel} />
                      </TableCell>

                      {/* Signed doc */}
                      <TableCell sx={{ ...tdSx, display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            fontSize: 12.5, fontWeight: 500,
                            color: signedContract ? T.successInk : T.ink500,
                          }}>
                            {signedContract ? (
                              <CheckCircleRoundedIcon sx={{ fontSize: 14, color: T.success }} />
                            ) : (
                              <HourglassEmptyRoundedIcon sx={{ fontSize: 14, color: T.ink400 }} />
                            )}
                            {signedContract ? 'Ngarkuar' : 'Pa dokument'}
                          </Box>
                          <Box sx={{ width: 8 }} />
                          <IconBtn
                            title="Shkarko të nënshkruarën"
                            disabled={!signedContract}
                            onClick={() => handleDownloadSigned(w.Contract)}
                          >
                            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconBtn>
                          <IconBtn
                            title="Ngarko dokumentin"
                            disabled={isUploading || !w?.Contract?.Id}
                            onClick={() => triggerHiddenInput(w)}
                          >
                            {isUploading ? <CircularProgress size={14} sx={{ color: T.brand }} /> : <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                          </IconBtn>
                        </Stack>
                      </TableCell>

                      {/* Word export */}
                      <TableCell sx={{ ...tdSx, display: { xs: 'none', lg: 'table-cell' } }}>
                        {w.Contract?.Template ? (
                          <IconBtn title="Shkarko si Word" onClick={() => getContractTemplate(w)}>
                            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconBtn>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: T.ink400 }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Custom field cells */}
                      {contractCFDefs.map(f => {
                        const cfVals = contractCFValuesMap[String(w.Contract?.Id)] || {};
                        return (
                          <TableCell key={f.key} sx={{ ...tdSx, display: { xs: 'none', xl: 'table-cell' } }}>
                            <Typography sx={{ fontSize: 13, color: T.ink700 }}>
                              {formatCFValue(f, cfVals[f.key])}
                            </Typography>
                          </TableCell>
                        );
                      })}

                      {/* Actions */}
                      <TableCell align="right" sx={{ ...tdSx, pr: 2.5 }}>
                        {w?.Contract?.Id ? (
                          <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
                            <IconBtn title="Detajet" to={`/contractlist/contract/${w.Contract.Id}`}>
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconBtn>
                            <IconBtn title="Ndrysho" to={`/contractlist/editcontract/${w.Contract.Id}`}>
                              <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconBtn>
                          </Stack>
                        ) : (
                          <Tooltip title="Shto kontratë" arrow placement="top">
                            <IconButton
                              size="small"
                              component={Link}
                              to={`/contractlist/addcontract?worker=${w.Id}`}
                              sx={{
                                width: 36, height: 36, borderRadius: '10px',
                                bgcolor: T.brandSoft, color: T.brand,
                                transition: 'all 160ms ease',
                                '&:hover': { bgcolor: T.brandLight, color: T.brandHover },
                              }}
                            >
                              <AddIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: `1px solid ${T.line}` }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rreshta për faqe:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            sx={{
              fontSize: 13,
              color: T.ink500,
              '& .MuiTablePagination-toolbar': { px: 2.5 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: 13,
                color: T.ink500,
              },
              '& .MuiTablePagination-select': { fontSize: 13, color: T.ink700 },
            }}
          />
        </Box>
      </Paper>

      {/* ─────────── Toast ─────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.variant === 'error' ? 'error' : toast.variant === 'warning' ? 'warning' : 'success'}
          onClose={() => setToast(t => ({ ...t, open: false }))}
          variant="filled"
          sx={{
            fontSize: 13.5,
            fontWeight: 500,
            borderRadius: '12px',
            boxShadow: '0 10px 24px rgba(15,23,42,.12)',
            alignItems: 'center',
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
